import { createColumnHelper } from '@tanstack/react-table';
import { InvestmentRequestUpdate } from 'client/cip/_openapi/portfolio-optimizer/models/InvestmentRequestUpdate';
import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { InferType, object, string } from 'yup';

import { Role } from '@ndvr/auth-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { FormSheetContent } from '@/components/FormSheet';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, useSheetClose } from '@/components/ui/sheet';
import { formatDate, formatDateTime, titleCase } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { DrawerMessage } from 'views/portfolio-engine/DrawerMessage';
import { PageSpinner, Toast } from 'widgets';
import {
    CondensedTable,
    DataGrid,
    FormLabel,
    FormSection,
    HorizontalLayout,
    requiredStringValidator,
    StaffOrPersonName,
    Title,
    Tooltip,
} from 'widgets';
import { FormButtonSelect } from 'widgets/form/input/FormButtonSelect';
import { FormTextarea } from 'widgets/form/input/FormTextArea';

import { RequestStatus } from '../../../cip/_openapi/portfolio-optimizer';
import { useInvestmentRequestData } from './investment-request-hooks';
import { useInvestmentRequestUpdates, useUpdateInvestmentRequest } from './investment-request-queries';
import { investmentRequestStatusToIcon } from './typeHelper';

const formSchema = object({
    status: requiredStringValidator.oneOf(Object.values(RequestStatus)),
    note: string(),
});

type FormType = InferType<typeof formSchema>;

const columnHelper = createColumnHelper<InvestmentRequestUpdate>();
const columns = [
    columnHelper.accessor('updatedAt', {
        header: 'Updated At',
        cell: ({ getValue }) => <Tooltip tooltip={formatDateTime(getValue())}>{formatDate(getValue())}</Tooltip>,
        sortingFn: 'datetime',
    }),
    columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => titleCase(getValue()),
    }),
    columnHelper.accessor('note', {
        header: 'Note',
        cell: ({ getValue }) => {
            const note = getValue();
            return note ? <div style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>{note}</div> : '';
        },
    }),
    columnHelper.accessor('lastUpdatedBy', {
        header: 'Updated By',
        cell: ({ getValue }) => {
            const lastUpdatedBy = getValue();
            return lastUpdatedBy ? <StaffOrPersonName guid={lastUpdatedBy} /> : 'System';
        },
    }),
];

export const ProcessInvestmentRequestDrawer = () => {
    const navigate = useNavigate();

    return (
        <Sheet defaultOpen onExitComplete={() => navigate('/trading/investment-requests', { replace: true })}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="xl">
                <ProcessInvestmentRequestContent />
            </SheetContent>
        </Sheet>
    );
};

const ProcessInvestmentRequestContent = () => {
    const closeSheet = useSheetClose();
    const { requestId } = useParams<{ requestId: string }>();

    // Parse and validate requestId
    const parsedRequestId = requestId ? parseInt(requestId, 10) : NaN;
    const isValidRequestId = !isNaN(parsedRequestId);

    // Role checks to determine if user should see all tenants or only current tenant
    const iopsPortalRole = useUserHasRole(Role.IOPS_PORTAL_ACCESS);
    const hasCustodyAccess = !!iopsPortalRole.data;
    const isRoleCheckPending = iopsPortalRole.isPending;

    // Use custom hook to fetch and transform investment request data
    // Apply tenant filtering if user doesn't have custody access
    const { getInvestmentRequestById, isLoading: isLoadingList } = useInvestmentRequestData({
        filterByTenant: !hasCustodyAccess,
    });

    // Always call hooks - use enabled flag to control fetching
    const { data: investmentRequestUpdates = [], isPending: investmentRequestUpdatesPending } =
        useInvestmentRequestUpdates(parsedRequestId);
    const { mutate: updateInvestmentRequest, isPending: savePending } = useUpdateInvestmentRequest();

    const investmentRequest = isValidRequestId ? getInvestmentRequestById(parsedRequestId) : undefined;

    const formDefaultValues = useMemo(
        () => ({
            status: investmentRequest?.status ?? RequestStatus.NEW,
            note: '',
        }),
        [investmentRequest?.status],
    );

    // Handle invalid request ID early
    if (!isValidRequestId) {
        return (
            <DrawerMessage title="Invalid Request">
                <Title content>Invalid Request</Title>
                <div>The investment request ID is invalid.</div>
                <Button onClick={closeSheet}>Back to List</Button>
            </DrawerMessage>
        );
    }

    // Handle loading state
    if (isLoadingList || isRoleCheckPending) {
        return (
            <DrawerMessage title="Loading investment request">
                <PageSpinner />
                <div>Loading investment request...</div>
            </DrawerMessage>
        );
    }

    // Handle not found or no permission
    if (!investmentRequest) {
        return (
            <DrawerMessage title="Access Denied">
                <Title content>Access Denied</Title>
                <div>You don&apos;t have permission to view this investment request.</div>
                <Button onClick={closeSheet}>Back to List</Button>
            </DrawerMessage>
        );
    }

    const getAllowedTransitions = (currentStatus: RequestStatus): RequestStatus[] => {
        switch (currentStatus) {
            case RequestStatus.NEW:
                return [
                    RequestStatus.NEW,
                    RequestStatus.IN_PROGRESS,
                    RequestStatus.PENDING_DELIVERY,
                    RequestStatus.COMPLETED,
                    RequestStatus.CANCELLED,
                ];
            case RequestStatus.IN_PROGRESS:
                return [
                    RequestStatus.IN_PROGRESS,
                    RequestStatus.PENDING_DELIVERY,
                    RequestStatus.COMPLETED,
                    RequestStatus.CANCELLED,
                ];
            case RequestStatus.PENDING_DELIVERY:
                return [
                    RequestStatus.PENDING_DELIVERY,
                    RequestStatus.IN_PROGRESS,
                    RequestStatus.COMPLETED,
                    RequestStatus.CANCELLED,
                ];
            default:
                return [currentStatus]; // For completed/cancelled, only allow staying in same state
        }
    };

    const allowedStatuses = getAllowedTransitions(investmentRequest.status);

    return (
        <FormSheetContent
            title="Process Investment Request"
            formKey={investmentRequest.requestId}
            formSchema={formSchema}
            defaultValues={formDefaultValues}
            pending={savePending}
            onSubmit={(formValues: FormType, close) =>
                updateInvestmentRequest(
                    {
                        investmentRequestUpdatePayload: {
                            requestId: investmentRequest.requestId,
                            status: formValues.status,
                            note: formValues.note,
                        },
                    },
                    {
                        onSuccess: close,
                        onError: (error) => {
                            Toast.forError(error, 'Error updating investment request');
                        },
                    },
                )
            }
        >
            <FormSection>
                <DataGrid
                    values={[
                        {
                            label: 'Account Name',
                            value: (
                                <Link to={`/account/${investmentRequest.portfolioGuid}`}>
                                    {investmentRequest.accountName}
                                </Link>
                            ),
                        },
                        {
                            label: 'Account Guid',
                            value: (
                                <CopiableGuid
                                    guid={investmentRequest.portfolioGuid}
                                    showFullGuid={true}
                                    showBorder={false}
                                />
                            ),
                        },
                        {
                            label: 'Custody Id',
                            value: (
                                <>
                                    <CopiableGuid
                                        guid={investmentRequest.custodyId || ''}
                                        showFullGuid={true}
                                        showBorder={false}
                                    />{' '}
                                    ({titleCase(investmentRequest.custodyType)})
                                </>
                            ),
                        },
                        { label: 'Created At', value: formatDate(investmentRequest.createdAt) },
                        {
                            label: 'Created By',
                            value: investmentRequest.createdBy ? (
                                <StaffOrPersonName guid={investmentRequest.createdBy} />
                            ) : (
                                'System'
                            ),
                        },
                        { label: 'Description', value: investmentRequest.description },
                        { label: 'Reason', value: titleCase(investmentRequest.reason) },
                    ]}
                    alignment="center"
                />
            </FormSection>
            <FormSection hasDivider>
                <FormButtonSelect
                    name="status"
                    label="Investment Request Status"
                    options={Object.values(RequestStatus).map((status) => ({
                        value: status,
                        disabled: !allowedStatuses.includes(status),
                    }))}
                    renderFn={(option, selected) => (
                        <HorizontalLayout $width="100%" $gap={12}>
                            {investmentRequestStatusToIcon(option.value as RequestStatus, selected)}{' '}
                            {titleCase(option.value as string)}
                        </HorizontalLayout>
                    )}
                />
                <FormTextarea name="note" label="Note" placeholder="Add a note about this update..." $height="100px" />
            </FormSection>
            <FormSection hasDivider>
                <FormLabel>History</FormLabel>
                <CondensedTable
                    columns={columns}
                    data={investmentRequestUpdates}
                    sort={{ col: 'updatedAt', desc: true }}
                    isLoading={investmentRequestUpdatesPending}
                />
            </FormSection>
        </FormSheetContent>
    );
};
